/**
 * "Trocar fotos" (E123.F3, GUS-128, ADR-0079 E.24): o sistema elege, o perito revisa.
 *
 * Abre a seleção das candidatas que já existia (agrupada pela classe lida, sem as repetidas) com a
 * candidata em uso marcada em cada grupo. Escolher outra elege a escolhida no grupo — a escolha do
 * perito substitui a do sistema — e o servidor refaz a conferência facial em segundo plano; a seção
 * das fotografias acompanha até o parecer novo.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { FotosDoSignatarioData, GrupoDaFoto } from '@/lib/fotos_do_signatario';

import { CandidatasDeImagem } from './CandidatasDeImagem';

const t = ptBR.exameEletronico.fotografias;

/** O código da imagem em uso em cada grupo: a eleita, identificada pelos bytes (ADR-0079 E.5). */
export function candidatasEmUso(fotos: FotosDoSignatarioData): Record<string, GrupoDaFoto> {
  const emUso: Record<string, GrupoDaFoto> = {};
  for (const grupo of fotos.grupos) {
    const eleita = grupo.fotos.find((f) => f.id === grupo.eleita_id);
    if (eleita !== undefined) emUso[eleita.content_sha256] = grupo.grupo;
  }
  return emUso;
}

export interface TrocarFotosProps {
  caseId: string;
  concluido: boolean;
  fotos: FotosDoSignatarioData;
  /** Uma candidata virou a escolhida de um grupo: a seção relê e acompanha a conferência. */
  onTrocada: () => void;
  onFechar: () => void;
  /** E191.F1: a troca de um grupo em voo (`null` ao terminar) — a seção de cima avisa. */
  onTrocando?: (grupo: GrupoDaFoto | null) => void;
}

export function TrocarFotos({
  caseId,
  concluido,
  fotos,
  onTrocada,
  onFechar,
  onTrocando,
}: TrocarFotosProps): JSX.Element {
  return (
    <div className="space-y-sc-3" data-testid="trocar-fotos" aria-labelledby="trocar-fotos-titulo">
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <h4 id="trocar-fotos-titulo" className="text-body text-fg">
          {t.trocaTitulo}
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onFechar}
          data-testid="trocar-fotos-fechar"
        >
          {t.fecharTroca}
        </Button>
      </div>
      <p className="max-w-prose text-body-sm text-fg-muted">{t.trocaIntro}</p>
      <CandidatasDeImagem
        caseId={caseId}
        concluido={concluido}
        onConfirmada={onTrocada}
        emUso={candidatasEmUso(fotos)}
        comLeituras={false}
        mensagemDaConfirmacao={t.trocada}
        onTrocando={onTrocando}
      />
    </div>
  );
}
